import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'bootstrap';
import './FormInitData.css';
import Breadcrumb from './components/Breadcrumb';
import { validateWizardStep } from './validation/wizardValidation';
import { saveForm } from './api/formApi';
import {
	getInitialFormData,
	STEP_SUCCESS,
	STEP_RECAP,
	WIZARD_STEP_COUNT_UI,
} from './constants/wizard';
import {
	StepProject,
	StepIssuer,
	StepServices,
	StepTouristData,
	StepSupport,
	StepDevices,
	StepWebSite,
	StepSocialNetwork,
	StepSubscriptions,
	StepFinance,
	StepVarious,
	StepContacts,
	StepFormulas,
	StepRecapitulative,
	StepSuccess,
} from './steps/wizardStepViews';

const FormInitData = () => {
	const { t, i18n } = useTranslation();
	const [stepState, setStepState] = useState({ step: 1, fromRecap: false });
	const setStep = useCallback((newStep, fromRecap = false) => {
		setStepState({ step: newStep, fromRecap });
	}, []);
	const { step, fromRecap } = stepState;

	const [formData, setFormData] = useState(getInitialFormData);
	const [errors, setErrors] = useState({});
	const [isStepValid, setIsStepValid] = useState(false);
	const formRef = useRef(null);
	const jsonFileInputRef = useRef(null);

	const runValidation = useCallback(
		(currentStep) => {
			const { errors: nextErrors, isValid } = validateWizardStep(currentStep, formData, t);
			setErrors(nextErrors);
			setIsStepValid(isValid);
			return isValid;
		},
		[formData, t],
	);

	useEffect(() => {
		document.documentElement.lang = i18n.language === 'en' ? 'en' : 'fr';
	}, [i18n.language]);

	useEffect(() => {
		runValidation(step);
	}, [formData, step, runValidation]);

	useEffect(() => {
		if (formRef.current) {
			const firstInput = formRef.current.querySelector(
				'input:not([type="file"]):not([disabled]), select:not([disabled]), textarea:not([disabled])',
			);
			if (firstInput) {
				firstInput.focus();
			}
		}
		document.querySelectorAll('[data-bs-toggle="tooltip"]').forEach((el) => {
			const inst = Tooltip.getOrCreateInstance
				? Tooltip.getOrCreateInstance(el)
				: Tooltip.getInstance(el) || new Tooltip(el);
			try {
				inst.hide();
			} catch (e) {
				/* ignore */
			}
		});
		document.querySelectorAll('.tooltip.show').forEach((el) => el.classList.remove('show'));
	}, [step]);

	const suivant = () => {
		if (runValidation(step)) {
			if (step < STEP_RECAP) {
				setStep(step + 1);
			}
		}
	};

	const precedent = () => {
		if (step > 1) {
			setStep(step - 1);
		}
	};

	const handleChange = useCallback((e) => {
		const { name, value, type } = e.target;
		let newValue = value;
		if (name === 'nom_projet') {
			newValue = value.replace(/[^a-z0-9-]/g, '').replace(/-+/g, '-');
		}
		if (name === 'finance_periodicity_entries' || name === 'finance_periodicity_sales') {
			newValue = parseInt(value, 10);
			if (Number.isNaN(newValue)) {
				newValue = 2;
			}
		}
		if (name === 'formules') {
			setFormData((prev) => ({ ...prev, formules: value }));
			return;
		}
		const checked = type === 'checkbox' ? e.target.checked : null;
		setFormData((prev) => ({
			...prev,
			[name]: type === 'checkbox' ? checked : newValue,
		}));
	}, []);

	const handleMultiSelect = (e) => {
		const { name, options } = e.target;
		const selected = [];
		for (let i = 0; i < options.length; i += 1) {
			if (options[i].selected) {
				selected.push(options[i].value);
			}
		}
		setFormData((prev) => ({ ...prev, [name]: selected }));
	};

	const handleImportJson = useCallback((file) => {
		if (!file) {
			return;
		}
		const reader = new FileReader();
		reader.onload = (ev) => {
			try {
				const text = ev.target?.result;
				if (typeof text !== 'string') {
					alert(t('app.alerts.readFile'));
					return;
				}
				const parsed = JSON.parse(text);
				if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
					alert(t('app.alerts.jsonObject'));
					return;
				}
				const base = getInitialFormData();
				const merged = { ...base, ...parsed };
				if (!Array.isArray(merged.formules) || merged.formules.length === 0) {
					merged.formules = base.formules;
				}
				if (!Array.isArray(merged.finance_currencies_iso) || merged.finance_currencies_iso.length === 0) {
					merged.finance_currencies_iso = base.finance_currencies_iso;
				}
				setFormData(merged);
				setErrors({});
			} catch {
				alert(t('app.alerts.jsonInvalid'));
			}
		};
		reader.onerror = () => {
			alert(t('app.alerts.readError'));
		};
		reader.readAsText(file, 'UTF-8');
	}, [t]);

	const renderStep = () => {
		switch (step) {
			case 1:
				return <StepProject data={formData} onChange={handleChange} errors={errors} />;
			case 2:
				return <StepIssuer data={formData} onChange={handleChange} errors={errors} setErrors={setErrors} />;
			case 3:
				return <StepServices data={formData} onChange={handleChange} errors={errors} setErrors={setErrors} />;
			case 4:
				return <StepTouristData data={formData} onChange={handleChange} errors={errors} setErrors={setErrors} validateStep={runValidation} />;
			case 5:
				return <StepSupport data={formData} onChange={handleChange} errors={errors} setErrors={setErrors} validateStep={runValidation} />;
			case 6:
				return <StepDevices data={formData} onChange={handleChange} errors={errors} setErrors={setErrors} />;
			case 7:
				return <StepWebSite data={formData} onChange={handleChange} onMultiSelect={handleMultiSelect} errors={errors} />;
			case 8:
				return <StepSocialNetwork data={formData} onChange={handleChange} errors={errors} />;
			case 9:
				return <StepSubscriptions data={formData} onChange={handleChange} errors={errors} setErrors={setErrors} validateStep={runValidation} />;
			case 10:
				return (
					<StepFinance
						data={formData}
						onChange={handleChange}
						onMultiSelect={handleMultiSelect}
						errors={errors}
					/>
				);
			case 11:
				return <StepVarious data={formData} onChange={handleChange} onMultiSelect={handleMultiSelect} errors={errors} />;
			case 12:
				return <StepContacts data={formData} onChange={handleChange} onMultiSelect={handleMultiSelect} errors={errors} />;
			case 13:
				return <StepFormulas data={formData} onChange={handleChange} errors={errors} setErrors={setErrors} />;
			case STEP_RECAP:
				return <StepRecapitulative data={formData} step={step} setStep={setStep} />;
			case STEP_SUCCESS:
				return <StepSuccess data={formData} />;
			default:
				return null;
		}
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		try {
			const response = await saveForm(formData);
			if (response.ok) {
				setStep(STEP_SUCCESS);
				window.scrollTo({ top: 0, behavior: 'smooth' });
			} else {
				alert(t('app.alerts.submitError'));
			}
		} catch (error) {
			console.error('Submit error', error);
			alert(t('app.alerts.submitError'));
		}
	};

	return (
		<>
			<div className="container">
				<div className="header d-flex flex-wrap align-items-start justify-content-between gap-2">
					<div>
					<img src="/img/logo-otipass-by-upcoop.png" alt={t('app.logoAlt')} className="logo" />
					<h1>{t('app.title')}</h1>
					<h2>{t('app.subtitle')}</h2>
					</div>
					<div className="ms-auto" lang={i18n.language === 'en' ? 'en' : 'fr'}>
						<label className="visually-hidden" htmlFor="form-lang-select">{t('app.langSelectLabel')}</label>
						<select
							id="form-lang-select"
							className="form-select form-select-sm"
							aria-label={t('app.langSelectLabel')}
							value={i18n.language && i18n.language.startsWith('en') ? 'en' : 'fr'}
							onChange={(e) => i18n.changeLanguage(e.target.value)}
						>
							<option value="fr">{t('app.langFr')}</option>
							<option value="en">{t('app.langEn')}</option>
						</select>
					</div>
				</div>
				<Breadcrumb step={step} setStep={setStep} />
				<form ref={formRef} onSubmit={handleSubmit}>
					{renderStep()}
					<div className="navigation-container">
						{fromRecap ? (
							<>
								<div className="page-indicator">
									{t('app.nav.step')} {step}/{WIZARD_STEP_COUNT_UI}
								</div>
								<button type="button" onClick={() => setStep(STEP_RECAP)} className="button-submit">
									{t('app.nav.save')}
								</button>
							</>
						) : (
							<>
								{step > 1 && step < STEP_SUCCESS && (
									<button type="button" onClick={precedent} className="button-precedent">
										{t('app.nav.previous')}
									</button>
								)}
								{step < STEP_SUCCESS && (
									<div className="page-indicator">
										{t('app.nav.step')} {step}/{WIZARD_STEP_COUNT_UI}
									</div>
								)}
								{step < STEP_RECAP ? (
									<button
										type="button"
										onClick={suivant}
										className={`button-suivant ${!isStepValid ? 'disabled' : ''}`}
										disabled={!isStepValid}
									>
										{step === 13 ? t('app.nav.recap') : t('app.nav.next')}
									</button>
								) : step === STEP_RECAP ? (
									<button type="button" onClick={handleSubmit} className="button-submit">
										{t('app.nav.send')}
									</button>
								) : null}
							</>
						)}
					</div>
				</form>
			</div>
			{step === 1 && (
				<div className="json-import-below-container">
					<input
						ref={jsonFileInputRef}
						type="file"
						accept=".json,application/json"
						className="visually-hidden"
						tabIndex={-1}
						aria-hidden="true"
						onChange={(e) => {
							const f = e.target.files?.[0];
							if (f) {
								handleImportJson(f);
							}
							e.target.value = '';
						}}
					/>
					<button
						type="button"
						className="btn btn-outline-secondary btn-sm"
						aria-label={t('app.jsonImport.aria')}
						onClick={() => jsonFileInputRef.current?.click()}
					>
						{t('app.jsonImport.button')}
					</button>
				</div>
			)}
		</>
	);
};

export default FormInitData;
